import React, { memo, useMemo } from 'react';

import { FieldGroup } from '@Sage/hoi-poi-ui';
import isTouched from './utils/isTouched';
import CrudField from './CrudField';

const MultipleCrudFieldV2 = ({
    label,
    labelMode,
    inputs = [],
    data,
    onChange,
    getError,
    errors,
    firstErrorField,
    isBulkAction,
    duplicates,
    dynamicHiddenMap,
    configMap,
    originalData,
    smartFieldOverridesCache,
}) => {
    const inputsEl = useMemo(
        () =>
            inputs.reduce((obj, field) => {
                if (
                    !field.hidden &&
                    !(dynamicHiddenMap && dynamicHiddenMap[field.fieldConfiguration])
                )
                    obj.push(CrudField);

                return obj;
            }, []),
        [dynamicHiddenMap, inputs],
    );
    const inputsProps = useMemo(
        () =>
            inputs.reduce((obj, field) => {
                // Use the smartFieldOverridesCache from parent
                const smartFieldOverrides = smartFieldOverridesCache?.get(field.id) || {};

                const { defaultValue, hidden, ...otherOverrides } = smartFieldOverrides;

                if (
                    hidden ||
                    field.hidden ||
                    (dynamicHiddenMap && dynamicHiddenMap[field.fieldConfiguration])
                )
                    return obj;

                let hintLiteral = field.hint;
                const inputAttrs = field.inputAttrs || {};
                if (field.mandatory) hintLiteral = hintLiteral + ' *';

                // Get parentValue dependency
                let parentValue = null;
                if (inputAttrs && inputAttrs.parentField) {
                    parentValue = data[inputAttrs.parentField];
                }

                const baseProps = {
                    label: field.label,
                    labelMode: field.labelMode,
                    type: field.type,
                    hint: hintLiteral,
                    description: field.description,
                    maxLength: field.dataLength,
                    limitInput: field.limitInput,
                    readOnly: field.readOnly,
                    hidden: field.hidden,
                    inputAttrs: inputAttrs,
                    value: data[field.id],
                    fieldId: field.id,
                    parentValue: parentValue,
                    onChange: onChange(field.id),
                    error: getError(errors[field.id], [field.dataLength]),
                    firstErrorField: firstErrorField && field.id.toLowerCase() === firstErrorField,
                    isBulkAction: isBulkAction,
                    duplicates: duplicates,
                    version: 2,
                };

                const hasDefaultValue = !!Object.entries(otherOverrides).length && !!defaultValue;
                const parentFieldId = configMap[field.smartField?.parentFieldId];
                const showSmartFieldDefault =
                    isTouched({ data, originalData, field: parentFieldId }) && hasDefaultValue;

                const finalValue = showSmartFieldDefault ? defaultValue : data[field.id];

                obj.push({
                    ...baseProps,
                    key: `crud-field-${field.id}-index`,
                    value: finalValue,
                    ...otherOverrides,
                });

                return obj;
            }, []),
        [
            configMap,
            data,
            duplicates,
            dynamicHiddenMap,
            errors,
            firstErrorField,
            getError,
            inputs,
            isBulkAction,
            onChange,
            originalData,
            smartFieldOverridesCache,
        ],
    );

    if (inputsProps.length === 0) return null;
    let classes = ['fm-multiple-fields'];

    return (
        <div className={classes.join(' ')}>
            <FieldGroup
                label={label}
                labelMode={labelMode}
                inputs={inputsEl}
                inputProps={inputsProps}
                isFullWidth
                fieldsMode="vertical"
            />
        </div>
    );
};

export default memo(MultipleCrudFieldV2);
